import { useEffect, useRef, useState } from 'react';
import { Clock, RefreshCw, ChevronRight } from 'lucide-react';
import { fetchHorariosDisponibles, HorarioDisponible } from '../../services/espacioServiciosService';
import { useSpaceAvailability } from '../../hooks/useWebSocket';
import './spaceDaySchedule.css';

const minutes = (value: string) => { const [h,m] = value.split(':').map(Number); return h*60+m; };
const timeLabel = (value: number) => `${String(Math.floor(value/60)).padStart(2,'0')}:${String(value%60).padStart(2,'0')}`;

export function suggestedStarts(slots: Pick<HorarioDisponible, 'inicio' | 'fin'>[], duration: number, earliest = 0) {
  const starts = new Set<string>();
  for (const slot of slots) {
    const first = Math.max(minutes(slot.inicio), earliest);
    // Include the exact start of a free interval as well as half-hour shortcuts.
    if (first + duration <= minutes(slot.fin)) starts.add(timeLabel(first));
    for (let n = Math.ceil(first/30)*30; n+duration<=minutes(slot.fin); n+=30) starts.add(timeLabel(n));
  }
  return [...starts].sort();
}

interface Props {
  spaceId: number;
  branchId?: number;
  date: string;
  durationMinutes: number;
  selectedStart: string | null;
  onSelect: (time: string) => void;
}
type Schedule = Awaited<ReturnType<typeof fetchHorariosDisponibles>>;

export default function SpaceDaySchedule({spaceId, branchId, date, durationMinutes, selectedStart, onSelect}: Props) {
  const [revision,setRevision] = useState(0);
  const [snapshot,setSnapshot] = useState<{key:string; data:Schedule} | null>(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState('');
  const [customTime,setCustomTime] = useState(false);
  const generation = useRef(0);
  const key = `${spaceId}|${date}`;
  useEffect(()=>setCustomTime(false),[key]);
  useSpaceAvailability(changedBranch => {
    if (!changedBranch || !branchId || changedBranch===branchId) setRevision(value=>value+1);
  });
  useEffect(()=>{
    const request = ++generation.current;
    if (!date) {setSnapshot(null);setLoading(false);setError('');return;}
    setLoading(true);setError('');
    fetchHorariosDisponibles(spaceId,date).then(data=>{
      if (generation.current===request) setSnapshot({key,data});
    }).catch(()=>{
      if (generation.current===request) setError('No pudimos consultar los horarios. Vuelve a intentarlo.');
    }).finally(()=>{if(generation.current===request)setLoading(false);});
    return ()=>{generation.current+=1;};
  },[spaceId,date,key,revision]);
  useEffect(()=>{
    if(!date)return;
    const refresh = ()=>{if(document.visibilityState==='visible')setRevision(value=>value+1);};
    const interval = setInterval(refresh,30000);
    window.addEventListener('focus',refresh);
    return ()=>{clearInterval(interval);window.removeEventListener('focus',refresh);};
  },[date,spaceId]);
  const data = snapshot?.key===key ? snapshot.data : null;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const starts = data ? suggestedStarts(data.horarios_disponibles,Math.max(15,durationMinutes),date===today ? now.getHours()*60+now.getMinutes()+1 : 0) : [];
  const blocks = data ? [
    ...data.horarios_disponibles.map(block=>({...block,occupied:false})),
    ...data.bloques_ocupados.map((block:{inicio:string;fin:string})=>({...block,occupied:true})),
  ].sort((a,b)=>a.inicio.localeCompare(b.inicio)) : [];

  const earliest = date===today ? now.getHours()*60+now.getMinutes()+1 : 0;
  const operationStart = data ? minutes(data.horario_operacion?.inicio || '08:00') : 480;
  const operationEnd = data ? minutes(data.horario_operacion?.fin || '20:00') : 1200;
  return <section className="space-day-schedule" aria-label="Agenda del espacio">
    {!date ? <div className="schedule-empty"><Clock size={30}/><h3>Tu horario empieza aquí</h3><p>Selecciona una fecha para consultar la agenda del espacio.</p></div> : <>
      <div className="schedule-heading"><div><span>AGENDA DEL DÍA</span><h3>{new Date(`${date}T12:00:00`).toLocaleDateString('es-BO',{day:'numeric',month:'long'})}</h3></div><button type="button" aria-label="Actualizar horarios del espacio" disabled={loading} onClick={()=>setRevision(value=>value+1)}><RefreshCw size={16}/></button></div>
      {loading && <p role="status" className="schedule-help">Actualizando horarios…</p>}
      {error && <p role="alert" className="schedule-error">{error}</p>}
      {data && !error && <>
        <div className="schedule-timeline" aria-hidden="true">{blocks.map((block,i)=><span key={i} className={block.occupied?'is-occupied':'is-free'} style={{left:`${(minutes(block.inicio)-operationStart)/(operationEnd-operationStart)*100}%`,width:`${(minutes(block.fin)-minutes(block.inicio))/(operationEnd-operationStart)*100}%`}}/>)}</div>
        <div className="schedule-axis"><span>{timeLabel(operationStart)}</span><span>{timeLabel(operationEnd)}</span></div>
        <div className="schedule-legend"><span><i/>Libre</span><span><i/>Ocupado</span></div>
        <div className="schedule-intervals" aria-label="Tramos libres y ocupados">{blocks.map((block,i)=>{
          const choices=block.occupied?[]:suggestedStarts([block],Math.max(15,durationMinutes),earliest);
          return block.occupied ? <div key={i} className="schedule-occupied"><span>{block.inicio} – {block.fin}</span><small>Ocupado</small></div> : <button type="button" key={i} className="schedule-free" aria-pressed={!!selectedStart&&selectedStart>=block.inicio&&selectedStart<block.fin} disabled={loading||!choices.length} onClick={()=>{setCustomTime(false);onSelect(choices[0]);}}><span>{block.inicio} – {block.fin}</span><small>Libre</small><ChevronRight size={16}/></button>;
        })}</div>
        {!starts.length && <p className="schedule-help">No hay horas sugeridas para este plan. Prueba otra fecha o espacio.</p>}
        <label className="schedule-start">Hora de inicio<select value={selectedStart||''} disabled={loading} onChange={event=>{setCustomTime(false);onSelect(event.target.value);}}><option value="">Selecciona una hora</option>{starts.map(time=><option key={time} value={time}>{time}</option>)}{selectedStart&&!starts.includes(selectedStart)&&<option value={selectedStart}>{selectedStart} · Hora exacta</option>}</select></label>
        <button type="button" className="schedule-custom" aria-expanded={customTime} onClick={()=>setCustomTime(value=>!value)}>{customTime?'Usar las horas sugeridas':'Escribir una hora exacta'}</button>
      </>}
      {customTime && <label className="schedule-start">Hora exacta<input type="time" value={selectedStart||''} onChange={event=>onSelect(event.target.value)}/></label>}
      <p className="schedule-help">Los horarios se actualizan automáticamente.</p>
    </>}
  </section>;
}
